import React, { useMemo } from 'react';
import { Card, Typography, Box, Grid, Stack, Chip, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { merge } from 'lodash';
import BaseOptionChart, { BaseChart } from 'src/components/charts/BaseOptionChart';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  border: `1px solid ${theme.palette.grey[200]}`,
  marginBottom: theme.spacing(2),
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 3,
  }
}));

// ----------------------------------------------------------------------

const DashboardStatsVisualization = ({ 
  title, 
  data, 
  type = 'bar',
  height = 300,
  showLegend = true,
  showDataLabels = false
}) => {
  const chartOptions = useMemo(() => {
    const baseOptions = merge(BaseOptionChart(), {
      chart: {
        type: type,
        height: height,
        toolbar: { show: false },
        animations: { enabled: false }
      },
      colors: ['#3182CE', '#38A169', '#E53E3E', '#805AD5', '#D69E2E', '#F56565', '#4299E1', '#48BB78'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '60%',
          distributed: false
        },
        pie: {
          donut: {
            size: '70%'
          }
        }
      },
      dataLabels: {
        enabled: showDataLabels,
        style: {
          fontSize: '12px',
          fontWeight: 600,
          colors: ['#fff']
        }
      },
      stroke: {
        width: type === 'line' ? 3 : 0,
        curve: 'smooth'
      },
      fill: {
        type: type === 'area' ? 'gradient' : 'solid',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: ['#fff'],
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.1,
          stops: [0, 100]
        }
      },
      xaxis: {
        categories: data?.categories || [],
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            colors: '#4A5568'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            colors: '#4A5568'
          },
          formatter: (value) => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
            return value.toString();
          }
        }
      },
      tooltip: {
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        y: {
          formatter: (value) => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
            if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
            return value.toLocaleString();
          }
        }
      },
      legend: {
        show: showLegend,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '13px',
        fontWeight: 500,
        markers: {
          radius: 6,
        },
        itemMargin: {
          horizontal: 12,
        },
        labels: {
          colors: '#4A5568'
        }
      },
      grid: {
        show: true,
        borderColor: '#E2E8F0',
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      }
    });

    return baseOptions;
  }, [type, height, showLegend, showDataLabels, data]);

  if (!data || !data.series || data.series.length === 0) {
    return (
      <StyledCard>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No data available
          </Typography>
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
        {title}
      </Typography>
      
      <Box sx={{ flex: 1 }}>
        <BaseChart
          series={data.series}
          options={chartOptions}
          type={type}
          height={height}
        />
      </Box>
    </StyledCard>
  );
};

// ----------------------------------------------------------------------

const StatsSummary = ({ stats = [] }) => {
  const totalValue = stats.reduce((sum, stat) => sum + (stat.value || 0), 0);
  const maxValue = Math.max(...stats.map(stat => stat.value || 0));

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
        Statistics Summary
      </Typography>
      <Stack spacing={2}>
        {stats.map((stat, index) => {
          const percentage = maxValue > 0 ? ((stat.value || 0) / maxValue) * 100 : 0;
          return (
            <StatItem key={index}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {stat.value >= 1000000 ? `${(stat.value / 1000000).toFixed(1)}M` : 
                     stat.value >= 1000 ? `${(stat.value / 1000).toFixed(1)}K` : 
                     stat.value.toLocaleString()}
                  </Typography>
                </Box>
                <ProgressBar 
                  variant="determinate" 
                  value={percentage} 
                  sx={{
                    '& .MuiLinearProgress-bar': {
                      background: `linear-gradient(90deg, ${stat.color || '#3182CE'} 0%, ${stat.color || '#3182CE'}80 100%)`,
                    }
                  }}
                />
              </Box>
              {stat.status && (
                <Chip 
                  label={stat.status} 
                  size="small" 
                  color={stat.status === 'Active' ? 'success' : 'default'}
                  sx={{ ml: 2 }}
                />
              )}
            </StatItem>
          );
        })}
      </Stack>
    </Box>
  );
};

export { DashboardStatsVisualization, StatsSummary };
export default DashboardStatsVisualization;
